import type React from "react";
import type { PropsWithChildren } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type DialogProps = PropsWithChildren<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>;

function Dialog({ open, children }: DialogProps) {
  if (!open) return null;
  return <>{children}</>;
}

function DialogContent({ children, className, onClose }: PropsWithChildren<{ className?: string; onClose?: () => void }>) {
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-accent/50 p-0 sm:items-center sm:p-6">
      <div className={cn("max-h-[85vh] w-full overflow-y-auto rounded-t-2xl border-2 border-accent/10 bg-card p-5 shadow-2xl sm:mx-auto sm:max-w-lg sm:rounded-2xl sm:p-6", className)}>
        {onClose ? (
          <div className="mb-3 flex justify-end">
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <X className="h-5 w-5" />
            </Button>
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mb-4 space-y-2", className)} {...props} />;
}

function DialogTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn("text-xl font-extrabold", className)} {...props} />;
}

function DialogDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-sm text-muted-foreground", className)} {...props} />;
}

export { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription };
